package io.github.erkko68.filament

import io.github.erkko68.filament.capi.*
import io.github.erkko68.filament.interop.*

/**
 * A SwapChain represents an Operating System's native renderable surface.
 *
 * Typically, it's a native window or a view. A SwapChain is initialized from a native object,
 * which must be of the proper type for each platform Filament is running on:
 *
 * Platform        | Native Type
 * :---------------|:----------------------------:
 * Android         | ANativeWindow*
 * macOS - OpenGL  | NSView*
 * macOS - Metal   | CAMetalLayer*
 * iOS - OpenGL    | CAEAGLLayer*
 * iOS - Metal     | CAMetalLayer*
 * X11             | Window
 * Windows         | HWND
 *
 * @see Engine.createSwapChain
 */
class SwapChain @InternalFilamentApi constructor(
    internal var nativeHandle: NativePointer,
    /** The platform window this swapchain was created over (Android's ANativeWindow), or [NullPointer]. */
    internal var window: NativePointer = NullPointer,
) {
    /** The native object, for interop with code calling the Fila* C API directly. Read-only: this wrapper owns it. */
    @InternalFilamentApi
    val nativeObject: NativePointer get() = nativeHandle

    /**
     * Frame rate compatibility strategy for [setFrameRate].
     */
    enum class FrameRateCompatibility {
        /** Default compatibility: the platform decides how to honor the requested rate. */
        DEFAULT,
        /** The content has a fixed source frame rate (e.g. video playback). */
        FIXED_SOURCE
    }
    /**
     * Strategy for applying a frame rate change that is not seamless.
     */
    enum class ChangeFrameRateStrategy {
        /** Only change the frame rate if the transition is seamless (no visual interruption). */
        ONLY_IF_SEAMLESS,
        /** Change the frame rate even if it requires a non-seamless transition. */
        ALWAYS
    }

    companion object {
        /** [Engine.createSwapChain] flag: a transparent swap chain. */
        const val CONFIG_TRANSPARENT: Long = 0x1
        /** [Engine.createSwapChain] flag: the swap chain can be read back (see [Renderer.readPixels]). */
        const val CONFIG_READABLE: Long = 0x2
        /** [Engine.createSwapChain] flag: the native window is an XCB window, not XLIB. */
        const val CONFIG_ENABLE_XCB: Long = 0x4
        /** [Engine.createSwapChain] flag: the native window is a CVPixelBufferRef (Metal). */
        const val CONFIG_APPLE_CVPIXELBUFFER: Long = 0x8
        /** [Engine.createSwapChain] flag: an sRGB swap chain; see [isSRGBSwapChainSupported]. */
        const val CONFIG_SRGB_COLORSPACE: Long = 0x10
        /** [Engine.createSwapChain] flag: the swap chain has a stencil component. */
        const val CONFIG_HAS_STENCIL_BUFFER: Long = 0x20
        /** [Engine.createSwapChain] flag: a protected swap chain; see [isProtectedContentSupported]. */
        const val CONFIG_PROTECTED_CONTENT: Long = 0x40
        /** [Engine.createSwapChain] flag: a 4x multisampled swap chain; see [isMSAASwapChainSupported]. */
        const val CONFIG_MSAA_4_SAMPLES: Long = 0x80

        /**
         * Checks if protected content (DRM) rendering is supported on this platform.
         *
         * Protected content rendering is required for secure video playback in some scenarios.
         *
         * @param engine The engine
         * @return true if protected content is supported, false otherwise
         */
        fun isProtectedContentSupported(engine: Engine): Boolean = FilaSwapChain_isProtectedContentSupported(engine.nativeHandle)
        /**
         * Checks if sRGB swap chain is supported on this platform.
         *
         * When supported, a SwapChain can be configured to automatically perform linear to sRGB
         * encoding in the hardware, which is more efficient than doing it in a shader.
         *
         * @param engine The engine
         * @return true if sRGB swap chain is supported, false otherwise
         */
        fun isSRGBSwapChainSupported(engine: Engine): Boolean = FilaSwapChain_isSRGBSwapChainSupported(engine.nativeHandle)
        /**
         * Checks if Multi-Sample Anti-Aliasing (MSAA) swap chain is supported.
         *
         * MSAA can be configured when creating a SwapChain on supported platforms (EGL on Android,
         * Metal). Other GL platforms (GLX, WGL, etc.) don't support swap chain MSAA because the
         * settings must be configured before window creation.
         *
         * @param engine The engine
         * @param samples Number of samples (e.g., 2, 4, 8)
         * @return true if MSAA with the specified sample count is supported, false otherwise
         */
        fun isMSAASwapChainSupported(engine: Engine, samples: Int): Boolean = FilaSwapChain_isMSAASwapChainSupported(engine.nativeHandle, samples)
    }

    /**
     * Gets the native window/surface associated with this SwapChain.
     *
     * The returned object type depends on the platform (e.g., ANativeWindow* on Android,
     * HWND on Windows, NSView* on macOS, etc.).
     *
     * @return The native window object, or null if not available
     */
    val nativeWindow: NativePointer? get() = FilaSwapChain_getNativeWindow(nativeHandle).takeIf { it != NullPointer }

    // One registry entry per callback kind, released only when the swapchain is destroyed:
    // releasing what the backend may still call for an in-flight frame would drop the callback.
    private var completed: (() -> Unit)? = null
    private var scheduled: (() -> Unit)? = null
    private var completedId = NullPointer
    private var scheduledId = NullPointer

    /**
     * Sets a callback to be invoked when a frame's rendering has completed on the GPU.
     *
     * The callback is called after the frame has been fully rendered on the GPU. On the default
     * handler, this is guaranteed to be called on the main Filament thread.
     *
     * Use this callback to be notified when GPU rendering is finished, useful for synchronization
     * or performance monitoring.
     *
     * Only the Metal backend supports this callback. Every other backend accepts it and never
     * calls it. (This is not true of [setFrameScheduledCallback], which fires on all of them.)
     *
     * @param callback The callback function to invoke when frame GPU rendering completes.
     *                 Pass null or a no-op function to unset the callback.
     */
    @PlatformGap(platforms = [FilamentPlatform.WEB], behavior = "never fires — OpenGLDriver (the WebGL backend) implements the frame-completed callback as a no-op.")
    fun setFrameCompletedCallback(callback: (() -> Unit)? = null) {
        completed = callback
        if (callback == null) {
            FilaSwapChain_setFrameCompletedCallback(nativeHandle, NullPointer, NullPointer, NullPointer)
            return
        }
        if (completedId == NullPointer) completedId = Callbacks.register(once = false) { _ -> completed?.invoke() }
        FilaSwapChain_setFrameCompletedCallback(nativeHandle, NullPointer, Callbacks.argUser, completedId)
    }

    /**
     * Sets a callback to be invoked when a frame is scheduled for presentation.
     *
     * The exact timing and behavior of this callback depends on the graphics backend:
     *
     * **Metal Backend:** Signifies that Filament has completed all CPU-side processing for the
     * frame and it is ready to be scheduled for presentation. The callback allows the application
     * to take control of presentation scheduling.
     *
     * **Other Backends (OpenGL, Vulkan, WebGPU):** Serves as a notification that CPU-side
     * processing is complete. Filament proceeds with normal presentation automatically.
     *
     * Only one callback per frame can be set. If called multiple times before Renderer.endFrame(),
     * the most recent call overwrites the previous one.
     *
     * @param callback The callback function to invoke when the frame is scheduled.
     *                 Pass null or a no-op function to unset the callback.
     */
    fun setFrameScheduledCallback(callback: (() -> Unit)? = null) {
        scheduled = callback
        if (callback == null) {
            FilaSwapChain_setFrameScheduledCallback(nativeHandle, NullPointer, NullPointer, NullPointer, 0L)
            return
        }
        if (scheduledId == NullPointer) scheduledId = Callbacks.register(once = false) { _ -> scheduled?.invoke() }
        FilaSwapChain_setFrameScheduledCallback(nativeHandle, NullPointer, Callbacks.userOnly, scheduledId, 0L)
    }

    // Called once the swapchain is destroyed and no further frame callbacks can fire.
    internal fun releaseCallbackStubs() {
        completed = null
        scheduled = null
        Callbacks.release(completedId)
        Callbacks.release(scheduledId)
        completedId = NullPointer
        scheduledId = NullPointer
    }

    /**
     * Checks if a frame scheduled callback is currently set on this SwapChain.
     *
     * @return true if a frame scheduled callback is set, false otherwise
     */
    val isFrameScheduledCallbackSet: Boolean get() = FilaSwapChain_isFrameScheduledCallbackSet(nativeHandle)

    /**
     * Returns whether this SwapChain supports the [setFrameRate] API.
     *
     * When a SwapChain is newly created, the surface capability may not yet be determined
     * by the underlying OS, in which case this returns INDETERMINATE. Once the platform completes surface
     * connection, this method authoritatively returns TRUE or FALSE.
     *
     * @return TRUE or FALSE once known, INDETERMINATE until then
     */
    @PlatformGap(platforms = [FilamentPlatform.IOS, FilamentPlatform.JVM, FilamentPlatform.WEB], behavior = "returns FALSE — only Android surfaces can switch the display frame rate.")
    val isFrameRateChangeSupported: Engine.FeatureState get() = Engine.FeatureState.entries[FilaSwapChain_isFrameRateChangeSupported(nativeHandle)]

    /**
     * Sets the intended frame rate for this SwapChain.
     *
     * Uses [FrameRateCompatibility.DEFAULT] and [ChangeFrameRateStrategy.ONLY_IF_SEAMLESS].
     *
     * @param frameRate The intended frame rate in frames per second. 0.0f clears/resets the rate.
     */
    @PlatformGap(platforms = [FilamentPlatform.IOS, FilamentPlatform.JVM, FilamentPlatform.WEB], behavior = "no effect — only Android surfaces can switch the display frame rate.")
    fun setFrameRate(frameRate: Float) =
        setFrameRate(frameRate, FrameRateCompatibility.DEFAULT, ChangeFrameRateStrategy.ONLY_IF_SEAMLESS)

    /**
     * Sets the intended frame rate for this SwapChain.
     *
     * @param frameRate     The intended frame rate in frames per second. 0.0f clears/resets the rate.
     * @param compatibility Frame rate compatibility mode.
     * @param strategy      Change strategy for non-seamless transitions.
     */
    @PlatformGap(platforms = [FilamentPlatform.IOS, FilamentPlatform.JVM, FilamentPlatform.WEB], behavior = "no effect — only Android surfaces can switch the display frame rate.")
    fun setFrameRate(frameRate: Float, compatibility: FrameRateCompatibility, strategy: ChangeFrameRateStrategy) {
        FilaSwapChain_setFrameRate(nativeHandle, frameRate, compatibility.ordinal, strategy.ordinal)
    }

}
